import { Component, Inject, Prop } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';

import JhiDataUtils from '@/shared/data/data-utils.service';
import AlertService from '@/shared/alert/alert.service';
import { TipoAccion } from '@/shared/model/enumerations/tipo-accion.model';
import camelCase from 'camelcase';
import { normalizeSync } from 'normalize-diacritics';
import { File } from '@/shared/model/file.model';
import { EstadoSolicitud } from '@/shared/model/enumerations/estado-solicitud.model';
import DocumentoService from '@/components/documentos/documento.service';
import { Configuracion } from '@/shared/model/configuracion/configuracion.model';
import { DmsApi, EtapaEnum, Archivo } from '@conacyt/dms-client';
import { Solucion } from '@/shared/model/solucion.model';
import { Solicitud, ISolicitudAcreditacionLigera } from '@/shared/model/solicitud.model';
import { MenuElement } from '@/shared/model/enumerations/menu-element.model';
import { PerfilApi } from '@conacyt/perfil-usuario-client';
import SolicitudService from '@/entities/solicitud/solicitud.service';

@Component
export default class DocumentosSniiComponent extends mixins(JhiDataUtils) {
  @Inject('documentoService') private readonly documentoService: () => DocumentoService;
  @Inject('alertService') private readonly alertService: () => AlertService;
  @Inject('dmsApi') public dmsApi: () => DmsApi;
  @Inject('perfilApi') public perfilApi: () => PerfilApi;
  @Inject('solicitudService') private solicitudService: () => SolicitudService;

  private readonly ETAPA: EtapaEnum = 'REGISTRO';
  public documentoModel: any = {};

  public isLoading = false;
  public currentFile: any = null;
  public currentContent: any = null;

  public isSaving = false;
  public currentFileIndex = -1;
  public currentLanguage = '';

  private readonly comprobanteDeNacionalidad = 'comprobanteDeNacionalidad';
  private readonly compAcreditacionActividades = 'comprobanteDeAcreditacion';
  private readonly comprobanteDeGradoDoctorado = 'comprobanteDeGradoAcademicoDoctorado';
  private readonly comprobanteDeGradoMaestria = 'comprobanteDeGradoAcademicoMaestria';
  private readonly comprobanteDeGradoEspecialidad = 'comprobanteDeGradoAcademicoEspecialidad';
  private readonly comprobanteDeGradoLicenciatura = 'comprobanteDeGradoAcademicoLicenciatura';
  public acreditacionActividades: ISolicitudAcreditacionLigera = {};
  public showDataAcreditacion = false;

  created(): void {
    if (this.solicitudId) {
      this.retrieveDocumento(this.solicitudId);
    } else {
      this.documentoModel = this.createProperties(this.documentoModel);
    }
  }

  public configuracion: Configuracion = new Configuracion();

  @Prop({ required: false })
  public solicitudId: string;

  @Prop({ required: true })
  public solicitud: Solicitud;

  @Prop({ default: EstadoSolicitud.EN_CAPTURA })
  public estadoSolicitud: EstadoSolicitud;

  @Prop({ required: false })
  accesos: any;

  @Prop({ default: false })
  public saving: boolean;

  @Prop({ default: false })
  public readOnly: boolean;

  public newFileName = '';

  @Prop({ required: false })
  public solucion: Solucion;

  public retrieveDocumento(solicitudId): void {
    this.isLoading = true;
    this.documentoService()
      .findBySolicitudId(solicitudId)
      .then(res => {
        let documentosFilterByTipoMaster = {};

        documentosFilterByTipoMaster = res;

        this.isLoading = false;
        this.getConfiguracion();

        this.documentoModel = this.createProperties(documentosFilterByTipoMaster);
      })
      .catch(error => {
        this.isLoading = false;
        this.documentoModel = this.createProperties(this.documentoModel);
      });
  }

  private getConfiguracion(): void {
    const preconfiguracion = this.solucion.componentes.find(com => com.menuName === MenuElement.DOCUMENTOS).configuracion;

    preconfiguracion.documentos = preconfiguracion?.documentos.filter(doc =>
      this.documentosRequeridosByTipoSolicitud.includes(doc.nombrePropiedad)
    );

    this.configuracion = preconfiguracion;
  }

  /**
   * Obtiene el tipo de solicitud
   */
  get isPermanencia() {
    return this.solicitud.SOLICITUD_RIZOMA.tipoSolicitud.id === 'PERMANENCIA';
  }

  /**
   * Obtiene la lista de los documentos requeridos y opcionales de cada tipo de solicitud y área.
   */
  get documentosRequeridosByTipoSolicitud() {
    if (
      this.solicitud.SOLICITUD_RIZOMA.tipoSolicitud.id === 'PERMANENCIA' &&
      this.solicitud.SOLICITUD_RIZOMA.areaConocimiento.area.clave === '3'
    ) {
      return [
        this.comprobanteDeNacionalidad,
        this.compAcreditacionActividades,
        this.comprobanteDeGradoDoctorado,
        this.comprobanteDeGradoMaestria,
        this.comprobanteDeGradoEspecialidad,
        this.comprobanteDeGradoLicenciatura,
      ];
    } else if (this.solicitud.SOLICITUD_RIZOMA.tipoSolicitud.id === 'PERMANENCIA') {
      return [this.comprobanteDeNacionalidad, this.compAcreditacionActividades, this.comprobanteDeGradoDoctorado];
    } else if (
      this.solicitud.SOLICITUD_RIZOMA.tipoSolicitud.id === 'INGRESO' &&
      this.solicitud.SOLICITUD_RIZOMA.areaConocimiento.area.clave === '3'
    ) {
      return [
        this.comprobanteDeNacionalidad,
        this.compAcreditacionActividades,
        this.comprobanteDeGradoDoctorado,
        this.comprobanteDeGradoMaestria,
        this.comprobanteDeGradoEspecialidad,
        this.comprobanteDeGradoLicenciatura,
      ];
    }
    return [this.comprobanteDeNacionalidad, this.compAcreditacionActividades, this.comprobanteDeGradoDoctorado];
  }

  /**
   * Se crean las propiedades de un documento
   * a partir de la configuración del archivo.
   * Es necesario utilizar el método this.$set()
   * para que las propiedades puedan ser reactivas
   *
   * @param model
   * @returns
   */
  public createProperties(model: any): any {
    if (this.configuracion) {
      // Se agregan las propiedades que provienen del archivo de configuracion
      this.configuracion.documentos.forEach(config => {
        if (!model[config.nombrePropiedad]) {
          this.$set(model, config.nombrePropiedad, new File());
        }
      });
    }
    return model;
  }

  get properties() {
    const propertiesWithConfig = Object.keys(this.documentoModel)
      .filter(property => property !== 'id')
      .map(property => {
        const propertyDefinition = { key: property, config: { title: null, max: null, mediaTypes: [], requerido: false, orden: 1000 } };
        const configurationIndex = this.configuracion.documentos.findIndex(config => config.nombrePropiedad === property);
        return configurationIndex === -1
          ? this.mapFromGeneralConfiguration(propertyDefinition, property)
          : this.mapFromConfiguration(propertyDefinition, configurationIndex);
      });

    return this.order(propertiesWithConfig);
  }

  private order(propertyDefinition: any[]) {
    let count = 0;
    this.configuracion.documentos.forEach(documento => {
      const index = propertyDefinition.findIndex(property => property.key === documento.nombrePropiedad);
      propertyDefinition[index].config.orden = count++;
    });

    return propertyDefinition.sort((leftProperty, rightProperty) => leftProperty.config.orden - rightProperty.config.orden);
  }
  private mapFromConfiguration(propertyDefinition: any, configurationIndex: number) {
    const config = this.configuracion.documentos[configurationIndex];
    propertyDefinition.config.title = config.nombre;
    propertyDefinition.config.max = config.max;
    propertyDefinition.config.mediaTypes = config.mediaTypes;
    propertyDefinition.config.requerido = !!config.requerido;
    return propertyDefinition;
  }
  private mapFromGeneralConfiguration(propertyDefinition: any, property: string) {
    propertyDefinition.config.title = this.documentoModel[property].title;
    propertyDefinition.config.max = this.configuracion.maxFileSize;
    propertyDefinition.config.mediaTypes = this.configuracion.mediaTypes;
    propertyDefinition.config.requerido = false;
    return propertyDefinition;
  }

  public addPropertyToModel(model: any, propertyName: string, nombreDocumento?: string) {
    const newFile = new File();
    newFile.title = nombreDocumento;
    this.$set(model, propertyName, newFile);
  }

  public save(isFullSave?: boolean): void {
    if (!this.solicitudId) {
      return;
    }
    this.isSaving = true;
    if (this.documentoModel.id) {
      this.documentoService()
        .update(this.documentoModel)
        .then(param => {
          this.isSaving = false;
          this.currentFileIndex = -1;
          this.documentoModel = param;
          if (isFullSave) {
            this.$emit('documentoSaved');
          }
          this.alertService().sectionUpdated(this, 'Documentos');
        })
        .catch(error => {
          this.isSaving = false;
          this.currentFileIndex = -1;
          this.alertService().showHttpError(this, error.response);
        });
    } else {
      this.documentoModel.id = this.solicitudId;
      this.documentoService()
        .create(this.documentoModel)
        .then(param => {
          this.documentoModel = param;
          this.isSaving = false;
          this.currentFileIndex = -1;
          this.alertService().sectionUpdated(this, 'Documentos');
        })
        .catch(error => {
          this.isSaving = false;
          this.currentFileIndex = -1;
          this.alertService().showHttpError(this, error.response);
        });
    }
  }

  public onReadFile(file, doc: File, fileIndex) {
    if (file) {
      this.currentFileIndex = fileIndex;
      this.toBase64(file, base64Data => {
        doc.nombre = file.name;
        doc.contentType = file.type;
        this.currentContent = base64Data;
        this.guardarDocumento(doc);
      });
    }
  }

  public guardarDocumento(doc: File) {
    const archivo: Archivo = { nombre: doc.nombre, contenido: this.currentContent };
    this.dmsApi()
      .agregaDocumento(this.configuracion.carpeta, this.solicitudId, this.userInfo.cvu, this.ETAPA, undefined, archivo)
      .then(res => {
        doc.uri = res.headers.location;
        this.currentContent = null;
        this.currentFile = null;
        this.currentFileIndex = -1;
        this.save();
      })
      .catch(error => {
        this.currentContent = null;
        this.currentFile = null;
        this.currentFileIndex = -1;
        this.alertService().showHttpError(this, error.response);
      });
  }

  public handleDeleteDocumento(doc: File, index) {
    this.currentFileIndex = index;
    this.dmsApi()
      .borraDocumentoPorUrl(doc.uri)
      .then(res => {
        doc.uri = null;
        doc.check = null;
        this.save();
      })
      .catch(error => {
        this.currentFileIndex = -1;
        this.alertService().showHttpError(this, error.response);
      });
  }

  public toFileExtension(mediaTypes: string[]) {
    if (!mediaTypes) {
      return '.pdf';
    }
    let fileExtensions = '';
    mediaTypes.forEach((type, index) => {
      if (index === 0) {
        fileExtensions = '.' + type;
      } else {
        fileExtensions = fileExtensions + ', .' + type;
      }
    });
    return fileExtensions;
  }

  public handleAddDocument() {
    const propertyName = camelCase(normalizeSync(this.newFileName));
    this.addPropertyToModel(this.documentoModel, propertyName, this.newFileName);
    this.newFileName = '';
    this.hideModal();
  }

  public handleOpenAddNewDocumentModal() {
    (this.$refs.addNewDocument as any).show();
  }
  public handleCloseAddDocumentModal() {
    this.hideModal();
  }

  public handleCancel(): void {
    this.hideModal();
  }

  public hideModal() {
    (this.$refs.addNewDocument as any).hide();
  }

  get canAddMoreDocuments(): boolean {
    if (
      this.configuracion.hasMoreDocuments &&
      this.configuracion.maxNumberOfDocuments &&
      this.documentsAvailable &&
      this.usuarioValidoAgregar
    ) {
      return true;
    }
    return false;
  }

  get documentsAvailable() {
    if (this.configuracion?.hasMoreDocuments && this.configuracion?.maxNumberOfDocuments) {
      return this.propertiesNotInConfiguration.length <= this.configuracion.maxNumberOfDocuments;
    }
    return false;
  }

  get propertiesNotInConfiguration() {
    const fullDocumentProperties = Object.keys(this.documentoModel);
    return fullDocumentProperties.filter(
      property => this.configuracion.documentos.findIndex(config => config.nombrePropiedad === property) === -1
    );
  }

  get userInfo() {
    return this.$store.getters.userInfo;
  }

  public isUpdating(fileIndex) {
    return this.currentFileIndex === fileIndex;
  }

  public showValidation(property: any) {
    const checkValue = this.documentoModel[property.key]?.check;
    return checkValue === false;
  }

  public disabledValidation() {
    if (this.accesos.acciones) {
      return (
        this.accesos.acciones.filter(accion => {
          return [TipoAccion.VALIDAR, TipoAccion.RECHAZAR, TipoAccion.APROBAR].includes(accion);
        }).length <= 0
      );
    }
    return true;
  }

  public options = [
    { text: 'Aceptar', value: true },
    { text: 'Rechazar', value: false },
  ];

  get login(): string {
    return this.$store.getters.account.login ?? '';
  }

  get usuarioValidoAgregar() {
    if (this.configuracion?.rolesAgregarDoc?.length >= 1) {
      return this.configuracion?.rolesAgregarDoc.some(rol => this.validaSolucionUsuarioRol(rol));
    }
    return true;
  }

  private validaSolucionUsuarioRol(rol): boolean {
    for (const autoridad of this.solucion.autoridades) {
      if (autoridad.usuarioId === this.login) {
        for (const rolesAut of autoridad.roles) {
          if (rolesAut === rol) {
            return true;
          }
        }
      }
    }
    return false;
  }

  /**
   * Consulta la acreditación de apeiron
   */
  public consultaAcreditacionApeiron(): void {
    this.solicitudService()
      .getSolicitudAcreditacionVigente(this.solicitud?.solicitante?.cvu)
      .then(r => {
        this.acreditacionActividades = r;
        this.showDataAcreditacion = true;
        this.alertService().showSuccess(this, this.$t('Consulta satisfactoria', 'Exitoso').toString());
      })
      .catch(err => {
        this.alertService().showError(this, 'Error al consultar acreditación apeiron');
      });
  }

  /**
   * Consulta la acreditación de rizoma
   */
  public consultaAcreditacionRizoma(): void {
    this.perfilApi()
      .getAcreditacionActividades()
      .then(r => {
        const AcreditacionRizoma: ISolicitudAcreditacionLigera = {
          situacion: r.data.situacion.nombre,
          fechaInicioSituacion: r.data.fechaInicioPeriodo,
          fechaFinSituacion: r.data.fechaFinPeriodo,
          institucion: r.data.institucion.nombre,
          dependencia: null,
          subDependencia: null,
          departamento: null,
          tipoNivel: r.data.institucion.tipoNivel.nombre,
          idTipoNivel: Number(r.data.institucion.tipoNivel.id),
          estadoInstitucion: r.data.institucion.estado.nombre,
          idEstadoInstitucion: Number(r.data.institucion.estado.id),
          urlComprobante: r.data.documento.uri,
          nombreComprobante: r.data.documento.nombre,
        };
        this.acreditacionActividades = AcreditacionRizoma;
        this.showDataAcreditacion = true;

        this.alertService().showSuccess(this, this.$t('Consulta satisfactoria', 'Exitoso').toString());
      })
      .catch(err => {
        this.alertService().showError(this, 'Error al consultar acreditación Rizoma');
      });
  }

  /**
   * Validación si puede actualizar la acreditación
   */
  get canUpdateAcreditacion() {
    const check = this.documentoModel?.comprobanteDeAcreditacion?.check;
    const url = this.documentoModel?.comprobanteDeAcreditacion?.uri;
    return !check && !url && this.solicitud.estado === EstadoSolicitud.EN_CORRECCION && this.isUserOwner;
  }

  /**
   * Actualiza la solicitud con la nueva acreditación
   */
  public updateAcreditacionApeiron(): void {
    const acreditacionActividadesDoc = {
      contentType: 'application/pdf',
      definicionDocumento: '1',
      nombre: this.acreditacionActividades.nombreComprobante,
      size: 1705678,
      uri: this.acreditacionActividades.urlComprobante,
    };
    this.solicitud.acreditacionActividades = this.acreditacionActividades;
    this.documentoModel.acreditacionActividades = acreditacionActividadesDoc;
    this.documentoModel.comprobanteDeAcreditacion = acreditacionActividadesDoc;
    this.solicitudService()
      .partialUpdate(this.solicitud)
      .then(() => this.alertService().showSuccess(this, this.$t('Actualización satisfactoria', 'Exitoso').toString()))
      .catch(() => this.alertService().showError(this, 'Error al actualizar'));
  }

  /**
   * Valida que el usuario logueado sea el mismo que el de la solicitud.
   */
  get isUserOwner() {
    return this.userInfo.cvu === this.solicitud.solicitante.cvu;
  }

  get puedeReemplazarDoc() {
    return this.solicitud.estado === EstadoSolicitud.EN_CORRECCION && this.isUserOwner;
  }
}
